import { ApartmentOutlined } from "@mui/icons-material";
import { Box, CircularProgress, Popover, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyButton } from "../../../../../components/UIToolkit/molecules/CopyButton";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { INK, NEUTRAL } from "../../../../../theme/tokens";
import type { ExternalSource, HrisIntegrationType } from "../../../../../utils/models";
import { type SftpDepartmentKeyStatus, useSftpDepartmentKeys } from "../sftpHooks";

type Props = {
	/** The connected pair whose file the codes are read from — the same one the preview is showing. */
	folder: string;
	file: string;
	source: ExternalSource;
	type: HrisIntegrationType;
};

/** Why a file carries no codes to list. `ok` is absent: it is the state that renders the list itself. */
const STATUS_MESSAGE_KEY: Record<Exclude<SftpDepartmentKeyStatus, "ok">, string> = {
	unsupported_source: "department.config.integrations.sftp.preview.deptIds.unsupported",
	file_not_found: "department.config.integrations.sftp.preview.deptIds.notFound",
	unparseable: "department.config.integrations.sftp.preview.deptIds.unparseable",
};

const PANEL_WIDTH = 340;
const PANEL_MAX_HEIGHT = 320;

/**
 * The department codes a previewed file actually contains, each with the company it names (DAT-810).
 *
 * The preview answers "is this the right file?"; this answers the question that immediately follows —
 * "and which of these codes is the department I am configuring?". Reading it off the file's own
 * routing column beats scrolling the grid: a network's roster carries every facility it owns, so the
 * codes are scattered over thousands of rows, and the code alone ("4288HE") is not something an
 * operator can recognize without the trading name beside it.
 *
 * Fetched only once the panel has been opened, and copyable rather than selectable: the Dept ID lives
 * on the source card (it belongs to the source, not to one feed), so the useful action here is
 * carrying the value over to it.
 */
export const SftpDepartmentKeysButton: FC<Props> = ({ folder, file, source, type }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	// Latched, not tied to `anchor`: closing the panel must not discard the answer we already paid for.
	const [hasOpened, setHasOpened] = useState(false);
	const { data, isFetching, error } = useSftpDepartmentKeys({ folder, file, source, type, enabled: hasOpened });

	const isOpen = !!anchor;

	return (
		<>
			<SystemButton
				buttonVariant="secondary"
				size="small"
				leadingIcon={<ApartmentOutlined fontSize="small" />}
				aria-haspopup="true"
				aria-expanded={isOpen}
				onClick={(event) => {
					setAnchor(event.currentTarget);
					setHasOpened(true);
					track("dept_config_sftp_dept_ids_opened", { department_id: chosenDepartment.department_id, source, type });
				}}
				data-testid="sftp-preview-dept-ids"
			>
				{t("department.config.integrations.sftp.preview.deptIds")}
			</SystemButton>
			{/* Raw MUI `Popover`, not `SystemReactTinyPopover`: this panel lives inside a `SystemDialog`, and
			    the house popover's outside-click handling is documented as unreliable across nested
			    portaled surfaces. The same call `SystemDataGrid`'s own `ColumnPickerPopover` makes. */}
			<Popover
				open={isOpen}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{ paper: { sx: { width: PANEL_WIDTH, maxHeight: PANEL_MAX_HEIGHT } } }}
			>
				<Box sx={{ p: 1 }} data-testid="sftp-preview-dept-ids-panel">
					{isFetching && !data ? (
						<Box sx={{ display: "flex", justifyContent: "center", p: 2 }} role="status">
							<CircularProgress size={20} />
						</Box>
					) : error ? (
						<Typography variant="body2" sx={{ p: 1, color: "error.main" }}>
							{t("department.config.integrations.sftp.preview.deptIds.error")}
						</Typography>
					) : data && data.status !== "ok" ? (
						<Typography variant="body2" sx={{ p: 1, color: INK.MUTED }}>
							{t(STATUS_MESSAGE_KEY[data.status])}
						</Typography>
					) : data?.keys.length === 0 ? (
						<Typography variant="body2" sx={{ p: 1, color: INK.MUTED }}>
							{t("department.config.integrations.sftp.preview.deptIds.empty")}
						</Typography>
					) : (
						data?.keys.map((key) => (
							<Box
								key={key.key}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									px: 1,
									py: 0.5,
									borderRadius: 1,
									"&:hover": { backgroundColor: NEUTRAL[100] },
								}}
								data-testid={`sftp-preview-dept-id-${key.key}`}
							>
								<Box sx={{ minWidth: 0, flex: 1 }}>
									<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
										{key.key}
									</Typography>
									{/* The company name is the whole point of the row — a bare code identifies nothing —
								    but a feed without a description column has none, and inventing one would read
								    like a name the operator could search the vendor system for. */}
									<Typography variant="caption" sx={{ color: INK.MUTED, display: "block" }} noWrap>
										{key.label
											? t("department.config.integrations.sftp.preview.deptIds.row", { company: key.label, people: key.employee_count })
											: t("department.config.integrations.sftp.preview.deptIds.rowNoCompany", { people: key.employee_count })}
									</Typography>
								</Box>
								<CopyButton
									value={key.key}
									size="dense"
									label={t("department.config.integrations.sftp.preview.deptIds.copy")}
									// The point of the panel is carrying a code over to the Dept ID box, so the copy —
									// not the open — is the action worth measuring.
									onCopied={() => track("dept_config_sftp_dept_id_copied", { department_id: chosenDepartment.department_id, source, type })}
									dataTestId={`sftp-preview-dept-id-copy-${key.key}`}
								/>
							</Box>
						))
					)}
				</Box>
			</Popover>
		</>
	);
};
